import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { OlModeProps, OlModeTable } from '@repo/toolkit/ol-mode-table-helper'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  ExclamationMarkOutlinedIcon,
  FamilyTwoIcon,
  MessageIcon,
  UserCanceledIcon,
  UserCheckIcon
} from '@repo/ui/icons'
import { LogSection } from '@repo/ui/log-section'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import RoundedIcon from '@repo/ui/rounded-icon'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { API } from '../../../../api/apis.config'
import { activateHostFamilies } from '../../../../api/host-families/activate-families'
import { deactivateHostFamilies } from '../../../../api/host-families/deactivate-families'
import { Paths } from '../../../../lib/constants/path'
import { useFilters } from '../../../../lib/hooks/use-filters'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  HostFamily,
  HostFamilyFilters
} from '../../../../lib/types/models/host-family/host-family'
import { HostFamilyLecComment } from '../../../../lib/types/models/host-family-lec-comment/host-family-lec-comment'
import { HostFamilyOlComment } from '../../../../lib/types/models/host-family-ol-comment/host-family-ol-comment'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformHostFamilyFiltersToUrl,
  transformUrlToHostFamilyFilters
} from '../../../../lib/utils/filters-mapping/host-family'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import FilterDrawer from '../../../elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import { countFilters } from '../../../elements/dynamic-table/utils/countFilters'
import { HostFamilyFilter } from '../../host-families/host-family-filter'
import { olNameColumn } from '../common/ol-columns'
import { HostFamilyDeactivationForm } from './host-family-deactivation-form'

type HostFamilyTableProps = OlModeProps

export function HostFamilyTable(props: HostFamilyTableProps) {
  const { t } = useTranslate()
  const tableRef = useRef<DynamicTableRef<HostFamily>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { navigateWithEvent } = useNavigator()

  const mode = new OlModeTable(props)

  const hostFamilyFilter: HostFamilyFilters = {
    olId: mode.getOlId(),
    deactivated: false
  }

  const {
    toggleModal: toggleComments,
    modalState: commentsModalState,
    closeModel: closeCommentsModal,
    selectedData: selectedComments
  } = useModalManager<
    'preview',
    { title: string; comments: (HostFamilyLecComment | HostFamilyOlComment)[] }
  >(['preview'])

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'deactivate',
    HostFamily[]
  >(['filters', 'deactivate'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<HostFamilyFilters>({
      initialFilters: hostFamilyFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformHostFamilyFiltersToUrl,
      transformUrlToFilter: transformUrlToHostFamilyFilters
    })

  const { mutateAsync: activateHostFamiliesMutation } = useMutation({
    mutationFn: activateHostFamilies,
    onError: showAxiosError
  })

  const { mutateAsync: deactivateHostFamiliesMutation } = useMutation({
    mutationFn: deactivateHostFamilies,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('deactivate')
    }
  })

  const baseColumns: Column<HostFamily>[] = [
    {
      accessorKey: 'deactivated',
      header: '',
      cell: ({ row }) => (
        <div className={'w-12 flex justify-center'}>
          {row.original.deactivated ? (
            <RoundedIcon variant={'danger'} icon={UserCanceledIcon} />
          ) : (
            <RoundedIcon variant={'success'} icon={UserCheckIcon} />
          )}
        </div>
      )
    },
    {
      accessorKey: 'familyName',
      header: 'NOM de famille',
      columnHeaderClassName: 'text-nowrap',
      enableSorting: true,
      cell: ({ row }) => (
        <span className={cn(row.original.deactivatedAsNotSuitable && 'text-red')}>
          {row.original.familyName.toUpperCase()}
        </span>
      )
    },
    {
      accessorKey: 'parent1FirstName',
      header: 'Prénom mère',
      enableSorting: true,
      cell: ({ row }) => (
        <span className={cn(row.original.deactivatedAsNotSuitable && 'text-red')}>
          {row.original.parent1FirstName}
        </span>
      )
    },
    {
      accessorKey: 'parent1PhoneNumber',
      header: 'Num tel mère',
      cell: ({ row }) =>
        row.original.parent1PhoneNumber ? formatPhoneNumber(row.original.parent1PhoneNumber) : '-'
    },
    {
      accessorKey: 'city',
      header: 'Ville',
      enableSorting: true
    },
    {
      accessorKey: 'yearsWithLec',
      header: 'Ancienneté (années)',
      columnHeaderClassName: 'text-nowrap',
      centerCell: true
    },
    {
      accessorKey: 'dateOfVisit',
      header: 'Date visite',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.dateOfVisit ? (
          <Badge text={DateFormatter.DDMMYYYY(row.original.dateOfVisit)} variant={'grey'} />
        ) : (
          '-'
        )
    },

    {
      accessorKey: 'studentsCount',
      header: 'Nº jeunes reçus',
      enableSorting: true,
      cell: ({ row }) => <div className={'text-center'}>{row.original.studentsCount}</div>
    },
    {
      accessorKey: 'olComments',
      header: 'Comm. OL',
      cell: ({ row }) =>
        row.original.olComments && row.original.olComments.length > 0 ? (
          <ActionButton
            icon={ExclamationMarkOutlinedIcon}
            variant={'lightPurple'}
            onClick={(e) => {
              e.stopPropagation()
              toggleComments('preview', {
                title: 'Commentaires OL',
                comments: row.original.olComments
              })
            }}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'lecComments',
      header: 'Comm. LEC',
      cell: ({ row }) =>
        row.original.lecComments && row.original.lecComments.length > 0 ? (
          <ActionButton
            icon={ExclamationMarkOutlinedIcon}
            variant={'lightPurple'}
            onClick={(e) => {
              e.stopPropagation()
              toggleComments('preview', {
                title: 'Commentaires LEC',
                comments: row.original.lecComments
              })
            }}
          />
        ) : (
          '-'
        )
    }
  ]

  const columns = mode.columns(baseColumns, [olNameColumn<HostFamily>()])

  const onRowClick = (hostFamily: HostFamily, event?: React.MouseEvent) => {
    navigateWithEvent(
      Paths.OL_HOST_FAMILY_INFORMATION.replace(':id', hostFamily.id.toString()).replace(
        ':olId',
        hostFamily.olId.toString()
      ),
      event
    )
  }

  return (
    <>
      <DynamicDataTable<HostFamily, HostFamilyFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Toutes les familles'}
        permissions={{ delete: false }}
        columns={columns}
        selectable
        allowSearchParam
        endpoint={API.hostFamilies.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['olId'])}
        onRowClick={onRowClick}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'multiple',
            variant: 'destructive',
            label: 'Désactiver la famille',
            fn: (hostFamilies) => toggleModal('deactivate', hostFamilies)
          },
          {
            type: 'multiple',
            variant: 'ghost',
            label: 'Activer la famille',
            refetch: true,
            fn: (hostFamilies) => activateHostFamiliesMutation(hostFamilies.map((hf) => hf.id))
          }
        ]}
      />

      {selectedComments && (
        <Modal
          isOpen={commentsModalState.preview}
          title={selectedComments.title}
          icon={MessageIcon}
          hideActions
          onCancel={() => closeCommentsModal('preview')}
        >
          <LogSection
            className={MODAL_BODY_CLASSNAME}
            data={selectedComments.comments.map((comment) => ({
              comment: comment.comment,
              updatedAt: new Date(comment.updatedAt)
            }))}
            cancelText={'Fermer'}
            handleReset={() => closeCommentsModal('preview')}
            dateFormatter={(date) => DateFormatter.ddMMyyyyHHmm(date)}
          />
        </Modal>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState.deactivate}
          title={t('host.family.forms.deactivation.label')}
          icon={FamilyTwoIcon}
          hideActions
          onCancel={() => closeModel('deactivate')}
        >
          <HostFamilyDeactivationForm
            defaultValues={{ ids: selectedData.map((hf) => hf.id) }}
            buttonsConfig={modalButtonConfig({
              confirmText: t('host.family.forms.deactivation.submit.label'),
              cancelText: t('host.family.forms.deactivation.cancel.label'),
              onCancel: () => closeModel('deactivate'),
              onConfirm: deactivateHostFamiliesMutation
            })}
          />
        </Modal>
      )}

      {/* Filters Modal */}
      <FilterDrawer<HostFamilyFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <HostFamilyFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </>
  )
}
